/** Catálogo de regras. Mesmas chamadas do `client/api.py`. */
import { apiFetch, type OpcoesRequisicao } from '@/lib/api'

/** `RuleCatalogResponse`: item do catálogo de regras (`/rules`). */
export interface RegraCatalogo {
  id: number
  code: string
  name: string
  category: string
  resource_types: string[]
  description: string | null
  is_active: boolean
  is_enabled: boolean
  fire_once_per_resource: boolean
  is_educational: boolean
}

/**
 * Mesma mensagem do `json.loads` do Python quando o corpo não é JSON
 * (ex.: "Expecting value: line 1 column 1 (char 0)" para HTML ou corpo vazio).
 */
function mensagemJsonInvalido(texto: string): string {
  const posicao = texto.length - texto.trimStart().length
  const antes = texto.slice(0, posicao)
  const linha = antes.split('\n').length
  const coluna = posicao - antes.lastIndexOf('\n')
  return `Expecting value: line ${linha} column ${coluna} (char ${posicao})`
}

/** Como o `r.json()` do httpx: corpo que não é JSON vira erro (o `apiFetch` devolveria o texto). */
async function buscarJson(caminho: string, opcoes?: OpcoesRequisicao): Promise<unknown> {
  const corpo = await apiFetch<unknown>(caminho, opcoes)
  if (corpo === null) throw new Error(mensagemJsonInvalido(''))
  if (typeof corpo === 'string') throw new Error(mensagemJsonInvalido(corpo))
  return corpo
}

/** Nome do tipo no Python, para a mensagem de validação. */
function tipoPython(valor: unknown): string {
  if (Array.isArray(valor)) return 'list'
  if (typeof valor === 'number') return Number.isInteger(valor) ? 'int' : 'float'
  if (typeof valor === 'boolean') return 'bool'
  if (typeof valor === 'object' && valor !== null) return 'dict'
  return 'str'
}

/** Texto do valor como o `repr` do Python (ex.: `{'detail': 'x'}`). */
function reprPython(valor: unknown): string {
  if (valor === null || valor === undefined) return 'None'
  if (typeof valor === 'boolean') return valor ? 'True' : 'False'
  if (typeof valor === 'number') return String(valor)
  if (typeof valor === 'string') {
    // O Python usa aspas simples, a não ser que o texto tenha aspas simples e não duplas.
    const aspas = valor.includes("'") && !valor.includes('"') ? '"' : "'"
    const escapado = valor
      .replace(/\\/g, '\\\\')
      .replace(/\n/g, '\\n')
      .replace(/\r/g, '\\r')
      .replace(/\t/g, '\\t')
    return aspas + (aspas === "'" ? escapado.replace(/'/g, "\\'") : escapado) + aspas
  }
  if (Array.isArray(valor)) return `[${valor.map(reprPython).join(', ')}]`
  const pares = Object.entries(valor as Record<string, unknown>).map(
    ([chave, item]) => `${reprPython(chave)}: ${reprPython(item)}`
  )
  return `{${pares.join(', ')}}`
}

/** Como o pydantic: valores longos mostram só o começo e o fim (25 + "..." + 24). */
function valorNaMensagem(valor: unknown): string {
  const texto = reprPython(valor)
  return texto.length > 50 ? `${texto.slice(0, 25)}...${texto.slice(-24)}` : texto
}

/** Um erro de validação, como os do pydantic (local, mensagem, tipo e valor recebido). */
interface ErroValidacao {
  local: (string | number)[]
  mensagem: string
  tipo: string
  valor: unknown
}

type Resultado<T> = { ok: true; valor: T } | { ok: false; erros: ErroValidacao[] }

function erro(tipo: string, mensagem: string, valor: unknown): Resultado<never> {
  return { ok: false, erros: [{ local: [], mensagem, tipo, valor }] }
}

/** Tipo do valor no Python (o `input_type` da mensagem). */
function tipoPythonDe(valor: unknown): string {
  if (valor === null || valor === undefined) return 'NoneType'
  return tipoPython(valor)
}

/** `int` do pydantic (modo flexível): aceita inteiro, bool, número sem fração e texto numérico. */
function validarInteiro(valor: unknown): Resultado<number> {
  if (typeof valor === 'boolean') return { ok: true, valor: valor ? 1 : 0 }
  if (typeof valor === 'number') {
    if (Number.isInteger(valor)) return { ok: true, valor }
    return erro(
      'int_from_float',
      'Input should be a valid integer, got a number with a fractional part',
      valor
    )
  }
  if (typeof valor === 'string') {
    const texto = valor.trim()
    if (/^[+-]?\d+(_\d+)*(\.0*)?$/.test(texto)) {
      return { ok: true, valor: Number(texto.replace(/_/g, '').replace(/\.0*$/, '')) }
    }
    return erro(
      'int_parsing',
      'Input should be a valid integer, unable to parse string as an integer',
      valor
    )
  }
  return erro('int_type', 'Input should be a valid integer', valor)
}

function validarTexto(valor: unknown): Resultado<string> {
  if (typeof valor === 'string') return { ok: true, valor }
  return erro('string_type', 'Input should be a valid string', valor)
}

const TEXTOS_VERDADEIROS = ['1', 'on', 't', 'true', 'y', 'yes']
const TEXTOS_FALSOS = ['0', 'off', 'f', 'false', 'n', 'no']

/** `bool` do pydantic (modo flexível): aceita 0/1 e textos como "true", "no", "on". */
function validarBooleano(valor: unknown): Resultado<boolean> {
  if (typeof valor === 'boolean') return { ok: true, valor }
  const naoInterpretado = () =>
    erro('bool_parsing', 'Input should be a valid boolean, unable to interpret input', valor)
  if (typeof valor === 'number') {
    if (valor === 0 || valor === 1) return { ok: true, valor: valor === 1 }
    return Number.isInteger(valor)
      ? naoInterpretado()
      : erro('bool_type', 'Input should be a valid boolean', valor)
  }
  if (typeof valor === 'string') {
    const texto = valor.toLowerCase()
    if (TEXTOS_VERDADEIROS.includes(texto)) return { ok: true, valor: true }
    if (TEXTOS_FALSOS.includes(texto)) return { ok: true, valor: false }
    return naoInterpretado()
  }
  return erro('bool_type', 'Input should be a valid boolean', valor)
}

function validarListaTextos(valor: unknown): Resultado<string[]> {
  if (!Array.isArray(valor)) return erro('list_type', 'Input should be a valid list', valor)
  const erros: ErroValidacao[] = []
  const itens: string[] = []
  valor.forEach((item: unknown, i) => {
    const r = validarTexto(item)
    if (r.ok) itens.push(r.valor)
    else erros.push(...r.erros.map((e) => ({ ...e, local: [i, ...e.local] })))
  })
  return erros.length ? { ok: false, erros } : { ok: true, valor: itens }
}

function validarTextoOuNulo(valor: unknown): Resultado<string | null> {
  return valor === null ? { ok: true, valor: null } : validarTexto(valor)
}

/** Campos do `RuleCatalogResponse`, na ordem do modelo. */
const CAMPOS_REGRA: [keyof RegraCatalogo, (valor: unknown) => Resultado<unknown>][] = [
  ['id', validarInteiro],
  ['code', validarTexto],
  ['name', validarTexto],
  ['category', validarTexto],
  ['resource_types', validarListaTextos],
  ['description', validarTextoOuNulo],
  ['is_active', validarBooleano],
  ['is_enabled', validarBooleano],
  ['fire_once_per_resource', validarBooleano],
  ['is_educational', validarBooleano],
]

/** Valida um item do catálogo como o `RuleCatalogResponse.model_validate`. */
function validarRegra(valor: unknown): Resultado<RegraCatalogo> {
  if (typeof valor !== 'object' || valor === null || Array.isArray(valor)) {
    return erro(
      'model_attributes_type',
      'Input should be a valid dictionary or object to extract fields from',
      valor
    )
  }
  const dados = valor as Record<string, unknown>
  const erros: ErroValidacao[] = []
  const regra: Record<string, unknown> = {}
  for (const [campo, validar] of CAMPOS_REGRA) {
    if (!Object.hasOwn(dados, campo)) {
      erros.push({ local: [campo], mensagem: 'Field required', tipo: 'missing', valor })
      continue
    }
    const r = validar(dados[campo])
    if (r.ok) regra[campo] = r.valor
    else erros.push(...r.erros.map((e) => ({ ...e, local: [campo, ...e.local] })))
  }
  return erros.length
    ? { ok: false, erros }
    : { ok: true, valor: regra as unknown as RegraCatalogo }
}

/** Mesmo texto do `str(ValidationError)` do pydantic. */
function mensagemValidacao(titulo: string, erros: ErroValidacao[]): string {
  const linhas = [`${erros.length} validation error${erros.length > 1 ? 's' : ''} for ${titulo}`]
  for (const e of erros) {
    if (e.local.length) linhas.push(e.local.join('.'))
    linhas.push(
      `  ${e.mensagem} [type=${e.tipo}, input_value=${valorNaMensagem(e.valor)}, input_type=${tipoPythonDe(e.valor)}]`
    )
    linhas.push(`    For further information visit https://errors.pydantic.dev/2.13/v/${e.tipo}`)
  }
  return linhas.join('\n')
}

/** `GET /rules`: catálogo completo (validado como `list[RuleCatalogResponse]`). */
export async function listarRegras(): Promise<RegraCatalogo[]> {
  return validarCatalogoRegras(await buscarJson('/rules'))
}

/**
 * Confere o corpo já lido do `GET /rules` como `list[RuleCatalogResponse]` (mesmas
 * mensagens do pydantic). Serve para quem lê o JSON do seu jeito (ex.: página de violações).
 */
export function validarCatalogoRegras(corpo: unknown): RegraCatalogo[] {
  const titulo = 'list[RuleCatalogResponse]'
  if (!Array.isArray(corpo)) {
    throw new Error(
      mensagemValidacao(titulo, [
        { local: [], mensagem: 'Input should be a valid list', tipo: 'list_type', valor: corpo },
      ])
    )
  }
  const erros: ErroValidacao[] = []
  const regras: RegraCatalogo[] = []
  corpo.forEach((item: unknown, i) => {
    const r = validarRegra(item)
    if (r.ok) regras.push(r.valor)
    else erros.push(...r.erros.map((e) => ({ ...e, local: [i, ...e.local] })))
  })
  if (erros.length) throw new Error(mensagemValidacao(titulo, erros))
  return regras
}

/** `RulePatch`: campos alteráveis do catálogo (omitir = não alterar). */
export interface AlteracaoRegra {
  is_enabled?: boolean
  fire_once_per_resource?: boolean
  is_educational?: boolean
  description?: string | null
}

/** `PATCH /rules/{id}`: altera habilitação, flags ou descrição da regra. */
export async function atualizarRegra(
  id: number,
  alteracao: AlteracaoRegra
): Promise<RegraCatalogo> {
  const corpo = await buscarJson(`/rules/${id}`, { method: 'PATCH', json: alteracao })
  const r = validarRegra(corpo)
  if (!r.ok) throw new Error(mensagemValidacao('RuleCatalogResponse', r.erros))
  return r.valor
}
